import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../authorization";
import { type Schema, zql } from "../generatedDrizzleSchema";
import {
  liveConnectorIdsBySpace,
  liveConnectorUsageRollupsBySpaceQuery,
  liveUsagesOfConnectorQuery,
} from "../liveConnectorUsage";
import {
  buildUserGroupMemberships,
  type GrantRow,
  grantsForPrincipal as grantsForPrincipalQuery,
  grantsForScope as grantsForScopeQuery,
} from "./grantQueries";
import type { Permission, ScopeKind } from "./permissions";
import {
  type BuiltinRoleId,
  type GrantLike,
  grantHasExactPermission,
} from "./roles";

export { type GrantRow, buildUserGroupMemberships } from "./grantQueries";

const q = (name: string) => `“${name}”`;
const SCOPE_LABELS: Record<ScopeKind, string> = {
  tenant: "tenant",
  space: "space",
  connected_app: "connector",
  skill: "skill",
  network: "network",
  user_group: "user group",
  service_account: "service account",
  oidc_provider: "OIDC provider",
};
const scopeLabel = (scope: ScopeKind) => SCOPE_LABELS[scope];

type CrossResourceAvailabilityTier = {
  dependentPermissions: readonly Permission[];
  supplierPermissions: readonly Permission[];
};

export type ScopedGrantRow = GrantRow & { scopeId: string };

type UsageFilter = { dependentId: string } | { supplierId: string };
type UsageEdge = { dependentId: string; supplierId: string };

/** Grant loads needed by the availability algorithm. Implemented by both the ZQL and Drizzle adapters. */
export interface GrantReader {
  grantsForScope(scopeId: string): Promise<ScopedGrantRow[]>;
  membershipGrants(
    userIds: readonly string[],
    groupScopeIds: readonly string[]
  ): Promise<ScopedGrantRow[]>;
}

/** Usage-graph reads. ZQL-only today (the mutator-side guards); Drizzle can add an adapter when something server-side needs it. */
interface UsageReader {
  spaceAppEdges(filter: UsageFilter): Promise<UsageEdge[]>;
  spaceSkillEdges(filter: UsageFilter): Promise<UsageEdge[]>;
  activePersonalChatUserIds(supplierId: string): Promise<Set<string>>;
}

/** Full read surface used by the ZQL guards. */
export interface RbacReader extends GrantReader, UsageReader {
  grantsForScopes(scopeIds: readonly string[]): Promise<ScopedGrantRow[]>;
  grantsForPrincipal(
    principal: { userId: string } | { userGroupId: string }
  ): Promise<ScopedGrantRow[]>;
}

type SyntheticDependent = { userId: string; permission: Permission };

type CrossResourceRule = {
  dependent?: { scope: ScopeKind };
  supplier: { scope: ScopeKind };
  tiers: readonly CrossResourceAvailabilityTier[];
  // A dependent uses a supplier through these edges (forward and reverse). Absent for rules whose
  // dependents are synthetic (not backed by role grants on a scope).
  usageEdges?: (
    usageReader: UsageReader,
    filter: UsageFilter
  ) => Promise<UsageEdge[]>;
  // The supplier role to auto-provision so a dependent grant meets its requirement. Absent for rules that don't provision.
  supplierRoleForGrant?: (grant: GrantLike) => BuiltinRoleId | null;
  // Dependents not represented as role grants (e.g. open personal chats), as (user, required permission) pairs.
  // Declaring this obliges per-user usage cleanup in connectorUsages.ts (dropUsagesLostBy*),
  // or the removal paths compute broken users and silently leave their rows behind.
  syntheticDependents?: (
    usageReader: UsageReader,
    supplierId: string
  ) => Promise<SyntheticDependent[]>;
  // When set, revoking a supplier grant is never blocked even if a dependent currently overlaps with it.
  skipRemovalEnforcement?: boolean;
  messages: {
    supplierUnavailable: (action: string, labels: readonly string[]) => string;
    principalLacksAccess: (
      principalKind: PrincipalKind,
      supplierNames: readonly string[]
    ) => string;
    principalCannotUse: (supplierName: string) => string;
  };
};

export type PrincipalKind = "user" | "group" | "service_account";

export function principalKindOf(grant: {
  userGroupId?: string | null;
  serviceAccountId?: string | null;
}): PrincipalKind {
  if (grant.userGroupId) return "group";
  if (grant.serviceAccountId) return "service_account";
  return "user";
}

function lacksAccessMessage(
  kind: PrincipalKind,
  supplierNoun: string,
  names: readonly string[],
  usedInSpace: boolean
): string {
  const noun = names.length > 1 ? `${supplierNoun}s` : supplierNoun;
  const list = names.map(q).join(", ");
  const who =
    kind === "group"
      ? "this group — it lacks"
      : kind === "service_account"
        ? "this service account — it lacks"
        : "this user — they lack";
  return `Cannot add ${who} access to ${noun} ${list}${usedInSpace ? " used in this space" : ""}`;
}

const CROSS_RESOURCE_AVAILABILITY = {
  space_uses_connected_apps: {
    dependent: { scope: "space" },
    supplier: { scope: "connected_app" },
    // Ordered strongest-first. Only editors (space:write) run or edit workflows,
    // so they must be able to use the connector (and view it). Managers (space:manage)
    // administer the space but cannot run workflows, so like viewers they only
    // need connected_app:read to see the connector — not to run it.
    tiers: [
      {
        dependentPermissions: ["space:write"],
        supplierPermissions: ["connected_app:use", "connected_app:read"],
      },
      {
        dependentPermissions: ["space:manage", "space:read"],
        supplierPermissions: ["connected_app:read"],
      },
    ],
    usageEdges: (usageReader, filter) => usageReader.spaceAppEdges(filter),
    // The UI's connectorGrantsForSpace may layer additional roles (e.g. connected_app_editor
    // and connected_app_sharer) on top as product policy; this returns only the minimum required role.
    supplierRoleForGrant: (grant) => {
      const required = requiredSupplierPermissions(
        "space_uses_connected_apps",
        grant
      );
      if (required.includes("connected_app:use")) return "connected_app_user";
      if (required.includes("connected_app:read"))
        return "connected_app_viewer";
      return null;
    },
    messages: {
      supplierUnavailable: (action, labels) =>
        `Cannot ${action} as space is missing connector access: ${labels.map((l) => q(l)).join(", ")}`,
      principalLacksAccess: (kind, names) =>
        lacksAccessMessage(kind, "connector", names, true),
      principalCannotUse: (name) =>
        `Connector ${q(name)} is not available in this scope`,
    },
  },
  personal_chat_uses_connected_apps: {
    supplier: { scope: "connected_app" },
    tiers: [
      {
        dependentPermissions: [],
        supplierPermissions: ["connected_app:use"],
      },
    ],
    syntheticDependents: async (usageReader, supplierId) =>
      [...(await usageReader.activePersonalChatUserIds(supplierId))].map(
        (userId) => ({
          userId,
          permission: "connected_app:use" as Permission,
        })
      ),
    messages: {
      supplierUnavailable: (_action, _labels) =>
        "Connector is not available in this scope",
      principalLacksAccess: (_kind, names) =>
        `Connector ${names.map(q).join(", ")} is not available in this scope`,
      principalCannotUse: (name) =>
        `Connector ${q(name)} is not accessible in your personal workflows`,
    },
  },
  // Not enforced anywhere: reachability is decided by egress routes, which
  // are authorized when plumbed. Kept for the network Access tab's space
  // role defaults.
  space_uses_networks: {
    dependent: { scope: "space" },
    supplier: { scope: "network" },
    tiers: [
      {
        dependentPermissions: ["space:write"],
        supplierPermissions: ["network:use"],
      },
    ],
    messages: {
      supplierUnavailable: (_action, labels) =>
        `Network is not available in this space: ${labels.map((l) => q(l)).join(", ")}`,
      // Unlike connectors and skills, a network is reached ambiently with no
      // member grant to auto-provision, so auto_grant_* can't cover it — say so
      // outright so an API caller doesn't hunt for a flag that would fix it.
      principalLacksAccess: (kind, names) =>
        `${lacksAccessMessage(kind, "network", names, false)}. Network access can’t be auto-granted; grant it separately first.`,
      principalCannotUse: (name) =>
        `Network ${q(name)} is not available in this scope`,
    },
  },
  space_uses_skills: {
    dependent: { scope: "space" },
    supplier: { scope: "skill" },
    tiers: [
      {
        dependentPermissions: ["space:read", "space:write", "space:manage"],
        supplierPermissions: ["skill:read"],
      },
    ],
    usageEdges: (usageReader, filter) => usageReader.spaceSkillEdges(filter),
    // Every space tier needs only skill:read, so the minimal supplier role is
    // always skill_user (returned directly rather than derived from
    // requiredSupplierPermissions, whose reference here would create a second
    // self-reference into this object that breaks its type inference).
    supplierRoleForGrant: () => "skill_user",
    // Skills are a build-time resource with no recorded usage — a space "using" a
    // skill is inferred from grant overlap, not a stored reference — so revoking a
    // skill grant just stops it appearing and is always allowed.
    skipRemovalEnforcement: true,
    messages: {
      supplierUnavailable: (action, labels) =>
        `Cannot ${action} as space is missing skill access: ${labels.map((l) => q(l)).join(", ")}`,
      principalLacksAccess: (kind, names) =>
        lacksAccessMessage(kind, "skill", names, true),
      principalCannotUse: (name) =>
        `Skill ${q(name)} is not available in this scope`,
    },
  },
} as const satisfies Record<string, CrossResourceRule>;

export type CrossResourceAvailabilityRuleId =
  keyof typeof CROSS_RESOURCE_AVAILABILITY;

export const CROSS_RESOURCE_RULE_IDS = Object.keys(
  CROSS_RESOURCE_AVAILABILITY
) as CrossResourceAvailabilityRuleId[];

export function crossResourceRule(
  ruleId: CrossResourceAvailabilityRuleId
): CrossResourceRule {
  return CROSS_RESOURCE_AVAILABILITY[ruleId];
}

function extractGroupIds(grants: readonly GrantRow[]): Set<string> {
  return new Set(grants.map((g) => g.userGroupId).filter(Boolean) as string[]);
}

export function dependentRequirementRank(
  ruleId: CrossResourceAvailabilityRuleId,
  grant: GrantLike
): number {
  const { tiers } = crossResourceRule(ruleId);
  for (let i = 0; i < tiers.length; i++) {
    if (
      tiers[i]!.dependentPermissions.some((p) =>
        grantHasExactPermission(grant, p)
      )
    ) {
      return i;
    }
  }
  return Number.POSITIVE_INFINITY;
}

export function requiredSupplierPermissions(
  ruleId: CrossResourceAvailabilityRuleId,
  grant: GrantLike
): readonly Permission[] {
  const rank = dependentRequirementRank(ruleId, grant);
  return rank === Number.POSITIVE_INFINITY
    ? []
    : crossResourceRule(ruleId).tiers[rank]!.supplierPermissions;
}

export function supplierRoleForGrant(
  ruleId: CrossResourceAvailabilityRuleId,
  grant: GrantLike
): BuiltinRoleId | null {
  return crossResourceRule(ruleId).supplierRoleForGrant?.(grant) ?? null;
}

type Principals = {
  userIds: Set<string>;
  groupIds: Set<string>;
  serviceAccountIds: Set<string>;
};

export function principalsWithPermission(
  grants: readonly GrantRow[],
  permission: Permission
): Principals {
  const userIds = new Set<string>();
  const groupIds = new Set<string>();
  const serviceAccountIds = new Set<string>();
  for (const g of grants) {
    if (!grantHasExactPermission(g, permission)) continue;
    if (g.userId) userIds.add(g.userId);
    else if (g.userGroupId) groupIds.add(g.userGroupId);
    else if (g.serviceAccountId) serviceAccountIds.add(g.serviceAccountId);
  }
  return { userIds, groupIds, serviceAccountIds };
}

function principalCovered(
  principal: {
    userId?: string | null;
    userGroupId?: string | null;
    serviceAccountId?: string | null;
  },
  userGroupIds: readonly string[],
  supplier: {
    userIds: ReadonlySet<string>;
    groupIds: ReadonlySet<string>;
    serviceAccountIds: ReadonlySet<string>;
  }
): boolean {
  if (principal.userGroupId)
    return supplier.groupIds.has(principal.userGroupId);
  // A service account holds only its own direct grants — it is never a group
  // member — so coverage requires a matching service-account grant on the
  // supplier.
  if (principal.serviceAccountId)
    return supplier.serviceAccountIds.has(principal.serviceAccountId);
  if (principal.userId) {
    return (
      supplier.userIds.has(principal.userId) ||
      userGroupIds.some((gId) => supplier.groupIds.has(gId))
    );
  }
  return false;
}

export function firstUnmetSupplierRequirement(
  ruleId: CrossResourceAvailabilityRuleId,
  dependentGrants: readonly GrantRow[],
  supplierGrants: readonly GrantRow[],
  userGroupMemberships: ReadonlyMap<string, readonly string[]>
): { grant: GrantRow; permission: Permission } | null {
  const supplierByPermission = new Map<Permission, Principals>();
  const supplierFor = (permission: Permission) => {
    let s = supplierByPermission.get(permission);
    if (!s) {
      s = principalsWithPermission(supplierGrants, permission);
      supplierByPermission.set(permission, s);
    }
    return s;
  };

  for (const grant of dependentGrants) {
    const groups = grant.userId
      ? (userGroupMemberships.get(grant.userId) ?? [])
      : [];
    for (const permission of requiredSupplierPermissions(ruleId, grant)) {
      if (!principalCovered(grant, groups, supplierFor(permission)))
        return { grant, permission };
    }
  }

  return null;
}

export function isSupplierAvailableFromGrants(
  ruleId: CrossResourceAvailabilityRuleId,
  dependentGrants: readonly GrantRow[],
  supplierGrants: readonly GrantRow[],
  userGroupMemberships: ReadonlyMap<string, readonly string[]>
): boolean {
  // A dependent that has principals but none that require the supplier can't
  // actually use it — e.g. a space with only managers/viewers under
  // space_uses_networks, which gates solely on space:write. Grantless dependents
  // stay vacuously available (access is decided elsewhere, e.g. a tenant admin
  // whose grant isn't on the scope).
  const relevant = dependentGrants.some(
    (grant) => requiredSupplierPermissions(ruleId, grant).length > 0
  );
  if (dependentGrants.length > 0 && !relevant) return false;
  return (
    firstUnmetSupplierRequirement(
      ruleId,
      dependentGrants,
      supplierGrants,
      userGroupMemberships
    ) === null
  );
}

export function principalHasSupplierPermissionFromGrants(
  userId: string,
  userGroupIds: readonly string[],
  supplierGrants: readonly GrantRow[],
  permission: Permission
): boolean {
  return principalCovered(
    { userId },
    userGroupIds,
    principalsWithPermission(supplierGrants, permission)
  );
}

// The single availability algorithm, shared by every back-end via GrantReader.
async function isSupplierAvailableFromGrantReader(
  grantReader: GrantReader,
  ruleId: CrossResourceAvailabilityRuleId,
  dependentScopeId: string,
  supplierScopeId: string
): Promise<boolean> {
  const [dependentGrants, supplierGrants] = await Promise.all([
    grantReader.grantsForScope(dependentScopeId),
    grantReader.grantsForScope(supplierScopeId),
  ]);

  const directUserIds = [
    ...new Set(dependentGrants.map((g) => g.userId).filter(Boolean)),
  ] as string[];
  const supplierGroupIds = extractGroupIds(supplierGrants);

  let userGroupMemberships: ReadonlyMap<string, readonly string[]> = new Map();
  if (directUserIds.length > 0 && supplierGroupIds.size > 0) {
    const memberGrants = await grantReader.membershipGrants(directUserIds, [
      ...supplierGroupIds,
    ]);
    userGroupMemberships = buildUserGroupMemberships(memberGrants);
  }

  return isSupplierAvailableFromGrants(
    ruleId,
    dependentGrants,
    supplierGrants,
    userGroupMemberships
  );
}

export async function userHasSupplierPermissionFromGrantReader(
  grantReader: Pick<RbacReader, "grantsForScope" | "grantsForPrincipal">,
  userId: string,
  supplierId: string,
  permission: Permission,
  excludeGroupId?: string
): Promise<boolean> {
  const supplierGrants = await grantReader.grantsForScope(supplierId);
  const supplierGroupIds = extractGroupIds(supplierGrants);
  let userGroupIds: string[] = [];
  if (supplierGroupIds.size > 0) {
    const userGrants = await grantReader.grantsForPrincipal({ userId });
    userGroupIds = userGrants
      .map((g) => g.scopeId)
      .filter(
        (id): id is string =>
          id != null && id !== excludeGroupId && supplierGroupIds.has(id)
      );
  }
  return principalHasSupplierPermissionFromGrants(
    userId,
    userGroupIds,
    supplierGrants,
    permission
  );
}

// Reader-level assertions shared by both back-ends; the ZQL and Drizzle wrappers below
// only differ in which GrantReader they construct.
export function defaultSupplierUnavailableMessage(
  ruleId: CrossResourceAvailabilityRuleId,
  supplierName?: string
): string {
  const rule = crossResourceRule(ruleId);
  const supplier = scopeLabel(rule.supplier.scope);
  const Supplier = `${supplier[0]!.toUpperCase()}${supplier.slice(1)}`;
  const dependent = rule.dependent
    ? scopeLabel(rule.dependent.scope)
    : "this scope";
  return supplierName
    ? `${Supplier} ${q(supplierName)} is not available in this ${dependent}`
    : `${Supplier} is not available in this ${dependent}`;
}

export async function assertSupplierAvailableFromGrantReader(
  grantReader: GrantReader,
  ruleId: CrossResourceAvailabilityRuleId,
  dependentScopeId: string,
  supplierScopeId: string,
  message?: string
): Promise<void> {
  if (
    await isSupplierAvailableFromGrantReader(
      grantReader,
      ruleId,
      dependentScopeId,
      supplierScopeId
    )
  )
    return;
  throw userFacingError(message ?? defaultSupplierUnavailableMessage(ruleId));
}

async function assertSuppliersAvailableToDependentFromGrantReader(
  grantReader: GrantReader,
  ruleId: CrossResourceAvailabilityRuleId,
  dependentId: string,
  suppliers: ReadonlyArray<{ id: string; label: string }>,
  action: string
): Promise<void> {
  const labels = await collectUnavailableSupplierLabels(suppliers, (id) =>
    isSupplierAvailableFromGrantReader(grantReader, ruleId, dependentId, id)
  );
  if (labels.length > 0) {
    throw userFacingError(
      crossResourceRule(ruleId).messages.supplierUnavailable(action, labels)
    );
  }
}

// --- ZQL adapter + Zero-facing wrappers ---

type ZqlTx = Pick<Transaction<Schema>, "run">;

export async function resolveSupplierName(
  tx: ZqlTx,
  ruleId: CrossResourceAvailabilityRuleId,
  supplierId: string
): Promise<string> {
  const scope = crossResourceRule(ruleId).supplier.scope;
  if (scope === "connected_app") {
    const [app] = await tx.run(zql.connectedApps.where("id", supplierId));
    return app?.name ?? supplierId;
  }
  if (scope === "skill") {
    const [skill] = await tx.run(zql.skills.where("id", supplierId));
    return skill?.name ?? supplierId;
  }
  if (scope === "network") {
    const [network] = await tx.run(zql.networks.where("id", supplierId));
    return network?.name ?? supplierId;
  }
  return supplierId;
}

type AvailabilityCheck = {
  tx: ZqlTx;
  tenantId: string;
  ruleId: CrossResourceAvailabilityRuleId;
  dependentScopeId: string;
  supplierScopeId: string;
};

export function zqlGrantReader(tx: ZqlTx, tenantId: string): RbacReader {
  const grantsForScope = (scopeId: string) =>
    tx.run(grantsForScopeQuery(scopeId, tenantId)) as Promise<ScopedGrantRow[]>;
  return {
    grantsForScope,
    grantsForScopes: (scopeIds) =>
      tx.run(
        zql.roleGrants
          .where("tenantId", tenantId)
          .where(({ cmp, or }) =>
            or(...[...scopeIds].map((id) => cmp("scopeId", id)))
          )
          .related("customRole", (cr) => cr.related("rolePermissions"))
      ) as Promise<ScopedGrantRow[]>,
    grantsForPrincipal: (principal) =>
      tx.run(grantsForPrincipalQuery(principal, tenantId)) as Promise<
        ScopedGrantRow[]
      >,
    membershipGrants: (userIds, groupScopeIds) =>
      tx.run(
        zql.roleGrants.where(({ and, or, cmp }) =>
          and(
            cmp("tenantId", tenantId),
            or(...[...userIds].map((id) => cmp("userId", id))),
            or(...[...groupScopeIds].map((id) => cmp("scopeId", id)))
          )
        )
      ) as Promise<ScopedGrantRow[]>,
    spaceAppEdges: (filter) => spaceAppEdgesZql(tx, filter),
    spaceSkillEdges: (filter) => spaceSkillEdgesZql(tx, tenantId, filter),
    activePersonalChatUserIds: (supplierId) =>
      activePersonalChatUserIdsZql(tx, supplierId),
  };
}

async function spaceAppEdgesZql(
  tx: ZqlTx,
  filter: UsageFilter
): Promise<UsageEdge[]> {
  const edges: UsageEdge[] = [];
  if ("dependentId" in filter) {
    const spaceId = filter.dependentId;
    const [branches, remotes] = await Promise.all([
      tx.run(liveConnectorUsageRollupsBySpaceQuery([spaceId])),
      tx.run(
        zql.connectedAppUsages
          .where("type", "space_git_remote")
          .where("refId", spaceId)
      ),
    ]);
    for (const appId of liveConnectorIdsBySpace(branches).get(spaceId) ?? [])
      edges.push({ dependentId: spaceId, supplierId: appId });
    for (const r of remotes)
      edges.push({ dependentId: spaceId, supplierId: r.connectedAppId });
  } else {
    const appId = filter.supplierId;
    const [stepUsages, remotes] = await Promise.all([
      tx.run(liveUsagesOfConnectorQuery(appId)),
      tx.run(
        zql.connectedAppUsages
          .where("type", "space_git_remote")
          .where("connectedAppId", appId)
      ),
    ]);
    for (const u of stepUsages)
      for (const branch of u.branchesAtHead)
        if (branch.workflow)
          edges.push({
            dependentId: branch.workflow.spaceId,
            supplierId: appId,
          });
    for (const r of remotes)
      edges.push({ dependentId: r.refId, supplierId: appId });
  }
  return edges;
}

async function spaceSkillEdgesZql(
  tx: ZqlTx,
  tenantId: string,
  filter: UsageFilter
): Promise<UsageEdge[]> {
  const rule = CROSS_RESOURCE_AVAILABILITY.space_uses_skills;
  const supplierPerms = rule.tiers.flatMap((t) => t.supplierPermissions);
  const dependentPerms = rule.tiers.flatMap((t) => t.dependentPermissions);

  const isDependentSide = "dependentId" in filter;
  const anchorId = isDependentSide ? filter.dependentId : filter.supplierId;
  const anchorPerms = isDependentSide ? null : supplierPerms;
  const crossPerms = isDependentSide ? supplierPerms : dependentPerms;

  const anchorGrants = await tx.run(grantsForScopeQuery(anchorId, tenantId));
  const principals: (
    | { userId: string }
    | { userGroupId: string }
    | { serviceAccountId: string }
  )[] = [];
  for (const g of anchorGrants) {
    if (anchorPerms && !anchorPerms.some((p) => grantHasExactPermission(g, p)))
      continue;
    if (g.userId) principals.push({ userId: g.userId });
    else if (g.userGroupId) principals.push({ userGroupId: g.userGroupId });
    else if (g.serviceAccountId)
      principals.push({ serviceAccountId: g.serviceAccountId });
  }
  if (principals.length === 0) return [];

  const allGrants = (
    await Promise.all(
      principals.map((p) => tx.run(grantsForPrincipalQuery(p, tenantId)))
    )
  ).flat();
  const crossIds = new Set<string>();
  for (const g of allGrants) {
    if (
      g.scopeId !== anchorId &&
      crossPerms.some((p) => grantHasExactPermission(g, p))
    )
      crossIds.add(g.scopeId);
  }
  if (crossIds.size === 0) return [];

  // When looking from a skill toward spaces (!isDependentSide), exclude
  // personal spaces — they shouldn't block skill role changes.
  if (!isDependentSide) {
    const spaces = await tx.run(
      zql.spaces.where(({ or, cmp }) =>
        or(...[...crossIds].map((id) => cmp("id", id)))
      )
    );
    const nonPersonalIds = new Set(
      spaces.filter((s) => s.mode !== "personal").map((s) => s.id)
    );
    for (const id of crossIds) {
      if (!nonPersonalIds.has(id)) crossIds.delete(id);
    }
  }

  return [...crossIds].map((id) =>
    isDependentSide
      ? { dependentId: anchorId, supplierId: id }
      : { dependentId: id, supplierId: anchorId }
  );
}

async function activePersonalChatUserIdsZql(
  tx: ZqlTx,
  appId: string
): Promise<Set<string>> {
  const rows = await tx.run(
    zql.connectedAppUsages
      .where("type", "personal_chat")
      .where("connectedAppId", appId)
      .whereExists("personalChat", (pc) => pc.where("closedAt", "IS", null))
      .related("personalChat")
  );
  return new Set(
    rows
      .map((row) => row.personalChat?.userId)
      .filter((id): id is string => id != null)
  );
}

export async function isSupplierAvailable(
  check: AvailabilityCheck
): Promise<boolean> {
  return isSupplierAvailableFromGrantReader(
    zqlGrantReader(check.tx, check.tenantId),
    check.ruleId,
    check.dependentScopeId,
    check.supplierScopeId
  );
}

export async function assertSupplierAvailable(
  check: AvailabilityCheck & { message?: string }
): Promise<void> {
  let message = check.message;
  if (!message) {
    const name = await resolveSupplierName(
      check.tx,
      check.ruleId,
      check.supplierScopeId
    );
    message = defaultSupplierUnavailableMessage(check.ruleId, name);
  }
  await assertSupplierAvailableFromGrantReader(
    zqlGrantReader(check.tx, check.tenantId),
    check.ruleId,
    check.dependentScopeId,
    check.supplierScopeId,
    message
  );
}

export async function assertPrincipalCanUseSupplier(
  tx: ZqlTx,
  tenantId: string,
  ruleId: CrossResourceAvailabilityRuleId,
  userId: string,
  supplierId: string
): Promise<void> {
  const r = crossResourceRule(ruleId);
  const flatTier = r.tiers.find((t) => t.dependentPermissions.length === 0);
  const grantReader = zqlGrantReader(tx, tenantId);
  for (const permission of flatTier?.supplierPermissions ?? []) {
    if (
      !(await userHasSupplierPermissionFromGrantReader(
        grantReader,
        userId,
        supplierId,
        permission
      ))
    ) {
      const name = await resolveSupplierName(tx, ruleId, supplierId);
      throw userFacingError(r.messages.principalCannotUse(name));
    }
  }
}

async function collectUnavailableSupplierLabels(
  suppliers: ReadonlyArray<{ id: string; label: string }>,
  isAvailable: (supplierId: string) => Promise<boolean>
): Promise<string[]> {
  const inaccessible: string[] = [];
  for (const s of suppliers) {
    if (!(await isAvailable(s.id))) inaccessible.push(s.label);
  }
  return inaccessible;
}

export async function assertSuppliersAvailableToDependent(
  tx: ZqlTx,
  tenantId: string,
  ruleId: CrossResourceAvailabilityRuleId,
  dependentId: string,
  suppliers: ReadonlyArray<{ id: string; label: string }>,
  action: string
): Promise<void> {
  await assertSuppliersAvailableToDependentFromGrantReader(
    zqlGrantReader(tx, tenantId),
    ruleId,
    dependentId,
    suppliers,
    action
  );
}
